/**
 * /auth/callback
 *
 * Receives the OAuth authorization code from the configured OIDC provider
 * after the user signs in. Exchanges the code for tokens via PKCE, then
 * redirects to the intended post-login target (default `/sessions`).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleCallback } from "@/lib/oidc";
import { useAuthStore } from "@/stores/authStore";
import { getAccessToken } from "@/lib/auth";
import { toast } from "@/hooks/useToast";

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const { signIn: storeSignIn } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const { redirectTo } = await handleCallback(redirectUri);
        const accessToken = getAccessToken();
        if (accessToken) storeSignIn(accessToken);

        void navigate(redirectTo, { replace: true });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Sign-in failed";
        setError(msg);
        toast({ variant: "destructive", title: "Sign-in failed", description: msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen p-4">
        <div className="max-w-md text-center space-y-2">
          <p className="text-destructive font-medium">Sign-in failed</p>
          <p className="text-sm text-muted-foreground">{error}</p>
          <a href="/" className="text-sm underline">
            Return to home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <p className="text-muted-foreground">Completing sign-in…</p>
    </div>
  );
}
